import { defineCustomElement } from 'vue';
import type { ComponentModule, ComponentsMap } from '../types/component';
import kebabCase from "kebab-case";

export interface IModule {
  default: any;
};

export type TModuleIterator = [string, IModule][];

export default class ComponentsLoader {
  private modules: Map<string, IModule> = new Map();

  constructor(
    private componentsMap: ComponentsMap,
    private useShadowRoot: boolean = true,
  ) { };

  public initComponents() {
    const modules: TModuleIterator = Array.from(this.modules.entries());
    return this.resolveModuleComponents(modules);
  }

  private resolveModuleComponents(modules: TModuleIterator) {
    const components: Set<string> = new Set();

    for (const [componentName, module] of modules) {
      if (!components.has(componentName)) {
        this.registerComponent(componentName, module);
        components.add(componentName);
      }
    }

    return Array.from(components.values());
  }
  
  private registerComponent(componentName: string, module: IModule) {
    const customElement = defineCustomElement(module.default, { shadowRoot: this.useShadowRoot });
    customElements.define(componentName, customElement);
  }

  public async loadingModules() {
    const components = this.componentsMap;
    
    const paths = Object.keys(components);
  
    return paths.map(async (path) => {
      const componentFileName = this.componentFileNameFromPath(path);
      const componentName = this.formatComponentName(componentFileName);
    
      const getModuleAsync: () => Promise<ComponentModule> = components[path];
  
      await getModuleAsync().then((module) => {
        this.modules.set(componentName, module);
      });
    });
  }
  
  private componentFileNameFromPath(path: string) {
    const componentFileName = path
      .split('/')
      .pop()
      ?.replace('.vue', '');
    
    if (!componentFileName) throw new Error('Component name is not defined');
    return componentFileName;
  }
  
  private formatComponentName(componentFileName: string) {
    let formatted = kebabCase(componentFileName);
    if (!formatted) throw new Error('Component name is bad');
    if (formatted[0] === '-') {
      formatted = formatted.slice(1);
    }
    if (formatted[formatted.length - 1] === '-') {
      formatted = formatted.slice(0, formatted.length - 1);
    }
    return formatted;
  }

}